import { ReactNode, useEffect, useState } from "react";
import { AlertTriangle, ChevronDown, ChevronRight, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import routeManifest from "@/data/route-manifest.json";
import { ExcludedRouteGroup, PageAnalysisResult, RouteManifestEntry } from "@/types/admin/page-views";
import {
  ANALYSIS_STEPS,
  formatDay,
  formatNumber,
  PERIOD_OPTIONS,
  ROUTE_KIND_CHIP_STYLES,
} from "./constants";
import FindingCard from "./FindingCard";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";
import type { GenieIconName } from "@/components/ui/genie-icons/genie-icons";

const ROUTES = routeManifest.routes as RouteManifestEntry[];
const JUDGED_COUNT = ROUTES.filter((r) => r.kind === "feature" || r.kind === "public").length;

const GradientCard = ({ children }: { children: ReactNode }) => (
  <div className="rounded-2xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] p-[2px] shadow-md shadow-genie-primary/20">
    <div className="rounded-[14px] bg-white p-5 md:p-6">{children}</div>
  </div>
);

const RunButton = ({ onRun, isPending, label }: { onRun: () => void; isPending: boolean; label: string }) => (
  <Button
    onClick={onRun}
    disabled={isPending}
    className="min-h-[44px] gap-2 rounded-xl bg-gradient-to-r from-genie-primary to-genie-secondary px-5 font-semibold text-white shadow-lg shadow-genie-primary/30 transition-all duration-200 hover:scale-[1.03] hover:from-genie-primary-hover hover:to-genie-secondary hover:shadow-xl hover:shadow-genie-primary/40"
  >
    <GenieIcon name="brain" size={16} color="#ffffff" label="" className={isPending ? "animate-pulse" : ""} />
    {label}
  </Button>
);

const SectionHeader = ({ genieIcon, title, count, description }: { genieIcon: GenieIconName; title: string; count: number; description: string }) => (
  <div className="mb-3 flex items-start gap-3">
    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#4E9CFF]/10 to-[#FF4DB8]/10">
      <GenieIcon name={genieIcon} size={18} gradientEnabled secondaryColor="#FF4DB8" label="" />
    </div>
    <div className="min-w-0">
      <h2 className="flex items-center gap-2 text-base font-semibold text-[#222B45]">
        {title}
        <span className="rounded-full bg-[#F9FAFC] px-2 py-0.5 text-xs font-semibold text-[#64748B]">{count}</span>
      </h2>
      <p className="text-sm text-[#64748B]">{description}</p>
    </div>
  </div>
);

const StatTile = ({ label, value, hint }: { label: string; value: string; hint: string }) => (
  <div className="rounded-xl border border-[#E5EAF0] bg-[#F9FAFC] px-4 py-3">
    <div className="text-xs font-medium text-[#64748B]">{label}</div>
    <div className="mt-0.5 text-xl font-bold tabular-nums text-[#222B45]">{value}</div>
    <div className="truncate text-xs text-gray-500">{hint}</div>
  </div>
);

const IntroCard = ({ onRun, isPending }: { onRun: () => void; isPending: boolean }) => {
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!isPending) return;
    setStep(0);
    const timer = window.setInterval(() => setStep((s) => (s + 1) % ANALYSIS_STEPS.length), 2600);
    return () => window.clearInterval(timer);
  }, [isPending]);

  return (
    <GradientCard>
      <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-[#4E9CFF]/15 to-[#FF4DB8]/15">
            <GenieIcon
              name="brain"
              size={26}
              gradientEnabled
              secondaryColor="#FF4DB8"
              label=""
              className={isPending ? "animate-pulse" : ""}
            />
          </div>
          <div className="space-y-1.5">
            <h2 className="text-lg font-semibold text-[#222B45]">Find the pages nobody opens</h2>
            {isPending ? (
              <p key={step} className="animate-in fade-in slide-in-from-bottom-1 text-sm font-medium text-genie-primary duration-500">
                {ANALYSIS_STEPS[step]}
              </p>
            ) : (
              <p className="max-w-2xl text-sm text-[#64748B]">
                Every visited URL is matched to its route. Per-genie pages, system paths, redirects and internal tools are
                set aside with their numbers; the {JUDGED_COUNT} feature and public pages are judged on their purpose,
                traffic, links and neighbours.
              </p>
            )}
          </div>
        </div>
        <RunButton onRun={onRun} isPending={isPending} label={isPending ? "Analyzing…" : "Run smart analysis"} />
      </div>
    </GradientCard>
  );
};

const ExcludedGroup = ({ group }: { group: ExcludedRouteGroup }) => {
  const [open, setOpen] = useState(false);
  const views = group.routes.reduce((sum, r) => sum + r.stats.views, 0);

  return (
    <div className="overflow-hidden rounded-2xl border border-[#E5EAF0] bg-white">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex min-h-[44px] w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-[#F9FAFC] md:px-5"
      >
        {open ? (
          <ChevronDown className="mt-0.5 h-4 w-4 shrink-0 text-[#64748B]" />
        ) : (
          <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-[#64748B]" />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${ROUTE_KIND_CHIP_STYLES[group.kind]}`}>
              {group.label}
            </span>
            <span className="text-xs text-[#64748B]">
              {group.routes.length} route{group.routes.length === 1 ? "" : "s"} · {formatNumber(views)} views
            </span>
          </div>
          <p className="mt-1 text-sm text-gray-600">{group.description}</p>
        </div>
      </button>
      {open && (
        <div className="overflow-x-auto border-t border-[#E5EAF0]">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#F9FAFC] text-left text-xs font-medium uppercase tracking-wide text-[#64748B]">
                <th className="px-4 py-2 md:px-5">Route</th>
                <th className="px-4 py-2 text-right md:px-5">Views</th>
                <th className="px-4 py-2 text-right md:px-5">URLs</th>
                <th className="px-4 py-2 text-right md:px-5">Last visit</th>
              </tr>
            </thead>
            <tbody>
              {group.routes.map((route) => (
                <tr key={route.path} className="border-t border-[#E5EAF0]">
                  <td className="px-4 py-2.5 md:px-5">
                    <div className="break-all font-mono text-[13px] text-[#222B45]">{route.path}</div>
                    <div className="break-all text-xs text-[#64748B]">
                      {route.redirectTo !== undefined ? `→ ${route.redirectTo}` : route.component}
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-right font-semibold tabular-nums text-[#222B45] md:px-5">
                    {formatNumber(route.stats.views)}
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-gray-600 md:px-5">
                    {formatNumber(route.stats.instances)}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-right text-gray-600 md:px-5">
                    {formatDay(route.stats.lastSeen)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const AnalysisResult = ({ result, onRun, isPending }: { result: PageAnalysisResult; onRun: () => void; isPending: boolean }) => {
  const { analysis, totals } = result;
  const periodLabel = PERIOD_OPTIONS.find((p) => p.value === result.period)?.label ?? `${result.from} – ${result.to}`;
  const partialCoverage = result.coverageDays < result.periodDays;
  const viewsHint =
    totals.previousViews === null ? "prior period not tracked" : `${formatNumber(totals.previousViews)} in prior period`;

  return (
    <div className="space-y-8">
      <GradientCard>
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div className="space-y-1.5">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-genie-primary">
              <GenieIcon name="brain" size={14} label="" />
              Summary · {periodLabel}
            </div>
            <p className="max-w-3xl text-[15px] leading-relaxed text-[#222B45]">{analysis.summary}</p>
          </div>
          <Button
            onClick={onRun}
            disabled={isPending}
            variant="outline"
            className="min-h-[44px] shrink-0 gap-1.5 border-[#E5EAF0] bg-white text-sm font-medium text-gray-600 hover:border-genie-primary/40 hover:bg-white hover:text-genie-primary"
          >
            <RefreshCw className={`h-4 w-4 ${isPending ? "animate-spin" : ""}`} />
            {isPending ? "Analyzing…" : "Run again"}
          </Button>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-5">
          <StatTile label="Views in period" value={formatNumber(totals.views)} hint={viewsHint} />
          <StatTile
            label="Pages judged"
            value={formatNumber(totals.analyzedRoutes)}
            hint={`${formatNumber(totals.excludedRoutes)} set aside by kind`}
          />
          <StatTile
            label="Tracking coverage"
            value={`${result.coverageDays}/${result.periodDays} days`}
            hint={result.trackingSince ? `since ${formatDay(result.trackingSince)}` : "no data yet"}
          />
          <StatTile
            label="Deletion candidates"
            value={formatNumber(analysis.candidates.length)}
            hint={`${analysis.caution.length} low-traffic pages to keep`}
          />
          <StatTile label="Unknown URLs" value={formatNumber(totals.unmatchedViews)} hint="views matching no route" />
        </div>

        {partialCoverage && (
          <div className="mt-4 flex gap-2.5 rounded-xl border border-genie-accent/40 bg-genie-accent/10 px-3.5 py-2.5 text-sm text-[#222B45]">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-genie-accent" />
            <span>
              Tracking only covers {result.coverageDays} of the {result.periodDays} days analyzed, so zero traffic
              proves less than it looks. Confidence is capped accordingly.
            </span>
          </div>
        )}
      </GradientCard>

      <section>
        <SectionHeader
          genieIcon="target"
          title="Deletion candidates"
          count={analysis.candidates.length}
          description="Feature and public pages whose purpose, traffic and discoverability all point to nobody using them."
        />
        {analysis.candidates.length > 0 ? (
          <div className="space-y-3">
            {analysis.candidates.map((finding) => (
              <FindingCard key={finding.path} variant="candidate" finding={finding} />
            ))}
          </div>
        ) : (
          <div className="rounded-2xl border border-dashed border-[#E5EAF0] bg-white px-5 py-8 text-center text-sm text-[#64748B]">
            Every judged page earns its place — nothing looks removable for this period.
          </div>
        )}
      </section>

      {analysis.caution.length > 0 && (
        <section>
          <SectionHeader
            genieIcon="lock"
            title="Low traffic, but keep"
            count={analysis.caution.length}
            description="Quiet pages where low numbers don't mean unused — flows, deep links and detail pages."
          />
          <div className="space-y-3">
            {analysis.caution.map((finding) => (
              <FindingCard key={finding.path} variant="caution" finding={finding} />
            ))}
          </div>
        </section>
      )}

      {result.excluded.length > 0 && (
        <section>
          <SectionHeader
            genieIcon="clipboard-check"
            title="Set aside by design"
            count={totals.excludedRoutes}
            description="Never judged as standalone pages — their traffic follows customers, flows or legacy links."
          />
          <div className="space-y-2">
            {result.excluded.map((group) => (
              <ExcludedGroup key={group.kind} group={group} />
            ))}
          </div>
        </section>
      )}

      {result.unmatched.length > 0 && (
        <section>
          <SectionHeader
            genieIcon="globe"
            title="Traffic to unknown URLs"
            count={result.unmatched.length}
            description="Visited URLs that match no route — old links, typos or removed pages landing on Not Found."
          />
          <div className="overflow-hidden rounded-2xl border border-[#E5EAF0] bg-white">
            {result.unmatched.map((row) => (
              <div
                key={row.path}
                className="flex items-center justify-between gap-3 border-b border-[#E5EAF0] px-4 py-2.5 last:border-0 md:px-5"
              >
                <span className="break-all font-mono text-[13px] text-[#222B45]">{row.path}</span>
                <span className="shrink-0 font-semibold tabular-nums text-[#222B45]">{formatNumber(row.views)}</span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

const AnalysisTab = ({
  result,
  isPending,
  onRun,
}: {
  result: PageAnalysisResult | undefined;
  isPending: boolean;
  onRun: () => void;
}) =>
  result ? (
    <AnalysisResult result={result} onRun={onRun} isPending={isPending} />
  ) : (
    <IntroCard onRun={onRun} isPending={isPending} />
  );

export default AnalysisTab;
